import { ReactNode } from "react";
import { AiOutlineClose } from "react-icons/ai";

export type ModalVariant = "primary" | "danger" | "warning" | "success";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  variant?: ModalVariant;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
}

const variantStyles: Record<ModalVariant, string> = {
  primary: "bg-[#0085BD]",
  danger: "bg-red-500",
  warning: "bg-amber-500",
  success: "bg-green-500",
};

export function Modal({
  isOpen,
  onClose,
  title,
  icon,
  variant = "primary",
  children,
  footer,
  maxWidth = "max-w-md",
}: ModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-lg ${maxWidth} w-full overflow-hidden shadow-xl`}
      >
        {/* Header */}
        <div
          className={`${variantStyles[variant]} px-6 py-4 flex items-center justify-between`}
        >
          <div className="flex items-center gap-3">
            {icon && <span className="text-white">{icon}</span>}
            <h3 className="text-lg font-semibold text-white">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white/10"
            aria-label="Fechar modal"
          >
            <AiOutlineClose size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Botões padronizados para o footer
interface ModalButtonProps {
  onClick: () => void;
  children: ReactNode;
  variant?: "cancel" | "danger" | "primary" | "success";
  disabled?: boolean;
  type?: "button" | "submit";
}

const buttonVariants: Record<string, string> = {
  cancel:
    "text-gray-600 hover:text-gray-800 border border-gray-300 hover:bg-gray-50",
  danger: "bg-red-500 text-white hover:bg-red-600",
  primary: "bg-[#0085BD] text-white hover:bg-[#006a96]",
  success: "bg-green-500 text-white hover:bg-green-600",
};

export function ModalButton({
  onClick,
  children,
  variant = "primary",
  disabled = false,
  type = "button",
}: ModalButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
        buttonVariants[variant]
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}
